import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { sdk } from '@/services/sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import ApperIcon from '@/components/ApperIcon';
import { toast } from 'sonner';

const FIELDS = [
  'Name',
  'service_c',
  'customerName_c',
  'phone_c',
  'status_c',
  'scheduledAt_c',
  'address_c',
  'notes_c',
  'verificationStatus_c',
  'customerRating_c',
  'customerReview_c',
  'workerResponse_c',
  'rejectionReason_c',
];

const STATUS_FLOW = {
  Pending: ['Accepted', 'Rejected'],
  Assigned: ['Accepted', 'Rejected'],
  Accepted: ['En route'],
  'En route': ['Arrived'],
  Arrived: ['In progress'],
  'In progress': ['Completed'],
  Completed: [],
  Rejected: [],
  Expired: [],
  Cancelled: [],
};

export const route = { path: '/jobs/:id', layout: 'owner', access: "profile === 'Worker_c'" };

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [otp, setOtp] = useState('');
  const [reviewDraft, setReviewDraft] = useState('');
  const [ratingDraft, setRatingDraft] = useState('');
  const [responseDraft, setResponseDraft] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const { data, loading, error, run } = useFetch(() => sdk.table('jobs_c').select(FIELDS).get(id).then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load this job');
    return r.data;
  }), [id]);
  const job = data;
  const nextStatuses = useMemo(() => STATUS_FLOW[job?.status_c] ?? [], [job?.status_c]);

  async function updateJob(changes, successMessage) {
    if (!job) return;
    setSaving(true);
    try {
      const result = await sdk.table('jobs_c').update({ Id: job.Id, ...changes });
      if (result.messages?.length) toast.error(result.messages[0]);
      if (!result.data?.length) return;
      toast.success(successMessage);
      await run();
    } catch (e) {
      toast.error(e.message || 'Could not save this update');
    } finally {
      setSaving(false);
    }
  }

  async function verifyCustomer() {
    if (!/^\d{4,6}$/.test(otp.trim())) {
      toast.error('Enter the 4–6 digit code provided by the customer.');
      return;
    }
    await updateJob({ verificationStatus_c: 'Verified' }, 'Customer verification recorded in demo mode');
    setOtp('');
  }

  async function changeStatus(status) {
    if (status === 'Rejected' && !rejectReason.trim()) {
      toast.error('Add a short reason before rejecting this order.');
      return;
    }
    if (status === 'Completed' && job.verificationStatus_c !== 'Verified') {
      toast.error('Verify the customer before marking this job complete.');
      return;
    }
    const changes = { status_c: status };
    if (status === 'Rejected') changes.rejectionReason_c = rejectReason.trim();
    await updateJob(changes, status === 'Accepted' ? 'Order accepted' : status === 'Rejected' ? 'Order rejected' : `Job marked ${status.toLowerCase()}`);
  }

  async function saveReview() {
    const rating = Number(ratingDraft || job.customerRating_c || 0);
    if (!rating) {
      toast.error('Choose the customer rating recorded for this visit.');
      return;
    }
    await updateJob({ customerRating_c: String(rating), customerReview_c: reviewDraft.trim(), workerResponse_c: responseDraft.trim() }, 'Review notes saved');
  }

  const mapUrl = job?.address_c ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.address_c)}` : null;
  const directionsUrl = job?.address_c ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address_c)}` : null;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <Link to="/jobs" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
        <ApperIcon name="ArrowLeft" size={16} /> Back to my jobs
      </Link>
      {loading ? (
        <div className="space-y-4 rounded-3xl border border-border bg-card p-7">
          <div className="h-8 w-1/2 animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-28 w-full animate-pulse rounded-2xl bg-muted" />
        </div>
      ) : error || !job ? (
        <div className="rounded-3xl border border-border bg-card p-7">
          <h1 className="font-heading text-2xl font-bold">Job not available</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error?.message || 'This job may have been removed or you may not have permission to view it.'}</p>
          <Button className="mt-5" onClick={run}>Try again</Button>
        </div>
      ) : (
        <>
          <section className="relative overflow-hidden rounded-3xl border border-white/15 bg-sidebar p-6 text-sidebar-foreground shadow-lg sm:p-8">
            <div className="pointer-events-none absolute -right-10 -top-12 h-52 w-52 rounded-full border-[32px] border-sidebar-accent/60" />
            <div className="relative flex flex-wrap items-start justify-between gap-5">
              <div className="max-w-2xl">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-sidebar-foreground/60">AryanFix Pro · Job verification</p>
                <h1 className="mt-3 font-heading text-3xl font-bold sm:text-4xl">{job.Name}</h1>
                <p className="mt-2 text-sm text-sidebar-foreground/75">{job.service_c} · {job.customerName_c}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Status value={job.status_c} />
                  <span className="rounded-full border border-sidebar-border px-3 py-1 text-xs">{job.verificationStatus_c || 'Verification pending'}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {job.phone_c && <Button asChild variant="outline" className="border-sidebar-border bg-transparent text-sidebar-foreground hover:bg-sidebar-accent"><a href={`tel:${job.phone_c}`}><ApperIcon name="Phone" size={15} className="mr-2" />Call customer</a></Button>}
                {directionsUrl && <Button asChild><a href={directionsUrl} target="_blank" rel="noreferrer"><ApperIcon name="Navigation" size={15} className="mr-2" />Navigate</a></Button>}
              </div>
            </div>
          </section>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
            <div className="space-y-5">
              <section className="rounded-2xl border border-border bg-card p-5 sm:p-6">
                <SectionTitle icon="ClipboardCheck" title="Accept or reject this order" subtitle="Choose a clear outcome. Rejection requires a reason." />
                {job.status_c === 'Pending' || job.status_c === 'Assigned' ? (
                  <div className="mt-5 space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Button disabled={saving} onClick={() => changeStatus('Accepted')}><ApperIcon name="Check" size={16} className="mr-2" />{saving ? 'Saving…' : 'Accept order'}</Button>
                      <div className="flex gap-2">
                        <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejecting" aria-label="Reason for rejecting order" />
                        <Button variant="destructive" disabled={saving || !rejectReason.trim()} onClick={() => changeStatus('Rejected')}>Reject</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 rounded-xl bg-muted/70 p-4 text-sm text-muted-foreground">This order has been {job.status_c?.toLowerCase()}. Available next steps are shown below.</div>
                )}
                {nextStatuses.filter((s) => !['Accepted', 'Rejected'].includes(s)).length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {nextStatuses.filter((s) => !['Accepted', 'Rejected'].includes(s)).map((status) => (
                      <Button key={status} disabled={saving || (status === 'Completed' && job.verificationStatus_c !== 'Verified')} variant={status === 'Completed' ? 'default' : 'outline'} onClick={() => changeStatus(status)}>
                        {status === 'En route' ? 'Start travel' : status === 'Arrived' ? 'Mark arrived' : status === 'In progress' ? 'Start service' : status === 'Completed' ? 'Complete job' : status}
                      </Button>
                    ))}
                  </div>
                )}
              </section>

              <section className="rounded-2xl border border-border bg-card p-5 sm:p-6">
                <SectionTitle icon="ShieldCheck" title="Customer verification" subtitle="Ask the customer for their job OTP before closing the visit." />
                <p className="mt-3 rounded-xl border border-warning-border bg-warning-muted/45 p-3 text-xs leading-5 text-warning-foreground">Preview safeguard: the OTP form below checks format only. A trusted server must validate the actual code before production service start or completion is accepted.</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <Input inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 4–6 digit customer OTP" aria-label="Customer verification OTP" disabled={job.verificationStatus_c === 'Verified'} />
                  <Button onClick={verifyCustomer} disabled={saving || job.verificationStatus_c === 'Verified'}>{job.verificationStatus_c === 'Verified' ? 'Verified' : saving ? 'Verifying…' : 'Verify customer'}</Button>
                </div>
                <p className="mt-3 text-xs leading-5 text-muted-foreground">Demo mode: this screen validates the code format only. Production OTP must be generated and verified by a trusted server; do not treat this demo as proof of identity.</p>
              </section>

              <section className="rounded-2xl border border-border bg-card p-5 sm:p-6">
                <SectionTitle icon="Star" title="Customer review" subtitle="Record review feedback only when it has actually been provided." />
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm font-medium">Rating recorded
                    <select name="customer-rating" value={ratingDraft || job.customerRating_c || ''} onChange={(e) => setRatingDraft(e.target.value)} className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm">
                      <option value="">Select rating</option>
                      {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} / 5 stars</option>)}
                    </select>
                  </label>
                  <label className="block text-sm font-medium">Customer review
                    <Input className="mt-2" value={reviewDraft || job.customerReview_c || ''} onChange={(e) => setReviewDraft(e.target.value)} placeholder="Exact customer feedback (optional)" />
                  </label>
                  <label className="block text-sm font-medium sm:col-span-2">Worker follow-up note
                    <Textarea className="mt-2" rows={3} value={responseDraft || job.workerResponse_c || ''} onChange={(e) => setResponseDraft(e.target.value)} placeholder="Optional response or follow-up action" />
                  </label>
                </div>
                <Button className="mt-4" disabled={saving || job.status_c !== 'Completed'} onClick={saveReview}>{saving ? 'Saving…' : 'Save review notes'}</Button>
                {job.status_c !== 'Completed' && <p className="mt-2 text-xs text-muted-foreground">Review notes unlock after the job is completed.</p>}
              </section>
            </div>

            <aside className="space-y-5">
              <section className="rounded-2xl border border-border bg-card p-5">
                <SectionTitle icon="MapPin" title="Customer location" subtitle="Open turn-by-turn directions in your map app." />
                <div className="mt-4 rounded-xl border border-border bg-muted/40 p-4">
                  <div className="flex gap-3"><div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><ApperIcon name="MapPinned" size={20} /></div><div className="min-w-0"><p className="text-sm font-semibold">{job.customerName_c}</p><p className="mt-1 text-sm leading-5 text-muted-foreground">{job.address_c || 'Customer address not provided'}</p></div></div>
                  {mapUrl && <div className="mt-4 flex flex-wrap gap-2"><Button asChild variant="outline"><a href={mapUrl} target="_blank" rel="noreferrer">View map</a></Button><Button asChild><a href={directionsUrl} target="_blank" rel="noreferrer">Get directions</a></Button></div>}
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                  <Info label="Scheduled visit" value={formatDate(job.scheduledAt_c)} />
                  <Info label="Contact number" value={job.phone_c || 'Not provided'} />
                  <Info label="Service notes" value={job.notes_c || 'No notes recorded.'} />
                </div>
              </section>
              {job.rejectionReason_c && <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4"><p className="text-xs font-bold uppercase tracking-wide text-destructive">Rejection reason</p><p className="mt-2 text-sm">{job.rejectionReason_c}</p></div>}
              <Link to="/tracking" className="flex items-center justify-between rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-muted/40"><div className="flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-xl bg-highlight/15 text-highlight"><ApperIcon name="Route" size={19} /></div><div><p className="text-sm font-semibold">Live travel tracking</p><p className="mt-1 text-xs text-muted-foreground">Start a foreground GPS session</p></div></div><ApperIcon name="ArrowUpRight" size={18} /></Link>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}

function SectionTitle({ icon, title, subtitle }) {
  return <div className="flex items-start gap-3"><div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><ApperIcon name={icon} size={19} /></div><div><h2 className="font-heading text-lg font-bold">{title}</h2><p className="mt-1 text-sm leading-5 text-muted-foreground">{subtitle}</p></div></div>;
}

function Info({ label, value }) {
  return <div><p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</p><p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6">{value}</p></div>;
}

function Status({ value }) {
  const style = value === 'Completed' ? 'bg-success-muted text-success' : value === 'Pending' ? 'bg-warning-muted text-warning-foreground' : value === 'Rejected' || value === 'Cancelled' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary';
  return <span className={`rounded-full px-3 py-1.5 text-xs font-bold ${style}`}>{value}</span>;
}

function formatDate(value) {
  if (!value) return 'Not scheduled';
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}
